/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { lodash, propsUtils } from '@ba-ui-toolkit/utils';
import TruncatedText from '@ba-ui-toolkit/truncatedtext';
import FlexLayout from '@ba-ui-toolkit/flexlayout';
import { Toggle as CarbonToggle, ToggleSmall as CarbonToggleSmall } from 'carbon';

const { uniqueId } = lodash;
const { sanitize, BaPropTypes } = propsUtils;

export default class ToggleSwitch extends Component {
	static propTypes = {
		id: PropTypes.string,
		tabIndex: PropTypes.number,
		label: PropTypes.node,
		align: PropTypes.oneOf(['left', 'right']), // toolkit concept, not carbon
		title: PropTypes.string,
		disabled: PropTypes.bool,
		indeterminate: BaPropTypes.deprecated('is ignored and not applicable to carbon toggle'),
		readOnly: PropTypes.bool,
		checked: PropTypes.bool,
		hover: BaPropTypes.deprecated('is ignored and not applicable to carbon toggle'),
		active: BaPropTypes.deprecated('is ignored and not applicable to carbon toggle'),
		onChange: PropTypes.func,
		small: PropTypes.bool,
		'aria-labelledby': PropTypes.string,
		className: PropTypes.string,
		style: PropTypes.object,
		checkboxInputExtraOptions: PropTypes.object,
	};

	static defaultProps = {
		checked: false,
		tabIndex: 0,
		align: 'left',
		className: '',
		style: {},
		checkboxInputExtraOptions: {},
	};

	_onChange = () => {
		!this.props.readOnly && this.props.onChange && this.props.onChange(!this.props.checked);
	};

	_getLabel = (labelId, htmlId, textLabel, isRight) => {
		const style = isRight ? { marginRight: '8px' } : { marginLeft: '8px' };

		return (
			<label id={labelId} htmlFor={htmlId} style={style}>
				<TruncatedText value={textLabel} />
			</label>
		);
	};

	render() {
		const {
			id,
			title,
			tabIndex,
			disabled,
			readOnly,
			checked,
			onChange,
			align,
			small,
			className,
			style,
			checkboxInputExtraOptions,
			...others
		} = this.props;
		const textLabel = this.props.label;
		const htmlId = id || uniqueId('__toggleSwitch_');

		const labelId = `${htmlId}_label`;
		const alignRight = align === 'right';
		const iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);

		const cProps = {
			id: htmlId,
			onChange: onChange && !iOS ? this._onChange : undefined,
			checked,
			tabIndex,
			disabled,
			readOnly,
			'aria-readonly': readOnly,
			'aria-labelledby':
				classnames(textLabel && labelId, this.props['aria-labelledby']) || null,
			labelA: '', // override default 'Off' label (not currently exposed)
			labelB: ''  // override default 'On' label (not currently exposed)
		};
		const hasLabel = textLabel != null;
		const Toggle = small ? CarbonToggleSmall : CarbonToggle;

		return (
			<FlexLayout
				{...sanitize(others, ToggleSwitch)}
				className={className}
				title={title !== textLabel ? title : undefined}
				onTouchStart={iOS ? this._onChange : undefined}
				style={style}
				alignItems="center"
				inline
			>
				{hasLabel && alignRight && this._getLabel(labelId, htmlId, textLabel, true)}
				<Toggle
					{...sanitize(checkboxInputExtraOptions, ToggleSwitch)}
					{...cProps}
				/>
				{hasLabel && !alignRight && this._getLabel(labelId, htmlId, textLabel, false)}
			</FlexLayout>
		);
	}
}
